import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Fade,
  CircularProgress,
  Typography,
  Chip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import MovieIcon from "@mui/icons-material/Movie";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import DescriptionIcon from "@mui/icons-material/Description";
import MoodIcon from "@mui/icons-material/Mood";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";

const SendMessage = ({
  onSend = (text, file) => console.log("SEND:", text, file),
  placeholder = "Type your message…",
  maxRows = 4,
}) => {
  const [text, setText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaAnchorEl, setMediaAnchorEl] = useState(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);

  const mediaOpen = Boolean(mediaAnchorEl);
  const emojiOpen = Boolean(emojiAnchorEl);

  // Close popovers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiAnchorEl &&
        !event.target.closest(".emoji-picker-container") &&
        !event.target.closest(`[aria-describedby="emoji-popover"]`)
      ) {
        setEmojiAnchorEl(null);
      }

      if (
        mediaAnchorEl &&
        !event.target.closest(`[aria-describedby="media-popover"]`) &&
        !event.target.closest(`[aria-describedby="emoji-popover"]`)
      ) {
        setMediaAnchorEl(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mediaAnchorEl, emojiAnchorEl]);

  useEffect(() => {
    // Clean up media recorder when component unmounts
    return () => {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }
      clearInterval(recordingTimerRef.current);
    };
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      trySend();
    }
  };

  const trySend = () => {
    const trimmed = text.trim();
    if (trimmed || selectedFile) {
      onSend(trimmed, selectedFile);
      setText("");
      setSelectedFile(null);
      inputRef.current?.focus();
      setMediaAnchorEl(null);
      setEmojiAnchorEl(null);
    }
  };

  const handleOpenMediaMenu = (event) => {
    setMediaAnchorEl(event.currentTarget);
    setEmojiAnchorEl(null);
  };

  const handleOpenEmojiMenu = (event) => {
    setEmojiAnchorEl(event.currentTarget);
    setMediaAnchorEl(null);
  };

  const handleCloseMenus = () => {
    setMediaAnchorEl(null);
    setEmojiAnchorEl(null);
  };

  const handleMediaSelect = (type) => {
    if (type === "audio-record") {
      toggleAudioRecording();
    } else {
      // Trigger file input
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = getAcceptString(type);
      fileInput.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          setSelectedFile(file);
        }
      };
      fileInput.click();
    }
    handleCloseMenus();
    inputRef.current?.focus();
  };

  const getAcceptString = (type) => {
    switch (type) {
      case "image":
        return "image/*";
      case "video":
        return "video/*";
      case "audio":
        return "audio/*";
      case "document":
        return ".pdf";
      default:
        return "*";
    }
  };

  const toggleAudioRecording = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // List of supported formats in order of preference
      const supportedFormats = [
        { mime: "audio/mp4; codecs=mp4a.40.2", ext: "m4a", type: "audio/mp4" }, // AAC in MP4
        { mime: "audio/mpeg", ext: "mp3", type: "audio/mpeg" },
        { mime: "audio/aac", ext: "aac", type: "audio/aac" },
        { mime: "audio/ogg; codecs=opus", ext: "ogg", type: "audio/ogg" },
      ];

      // Find the first supported format
      const selectedFormat = supportedFormats.find((f) =>
        MediaRecorder.isTypeSupported(f.mime)
      );

      if (!selectedFormat) {
        throw new Error("No compatible recording format supported by browser");
      }

      console.log(`Recording in ${selectedFormat.ext} format`);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedFormat.mime,
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: selectedFormat.type,
        });

        const audioFile = new File(
          [audioBlob],
          `recording.${selectedFormat.ext}`,
          {
            type: selectedFormat.type,
          }
        );

        setSelectedFile(audioFile);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Recording error:", err);

      // Specific error messages
      if (err.name === "NotSupportedError") {
        alert("Your browser doesn't support any of the required audio formats");
      } else if (err.name === "NotAllowedError") {
        alert("Microphone access was denied");
      } else {
        alert("Recording failed: " + err.message);
      }

      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const addEmoji = (emoji) => {
    setText(text + emoji.native);
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (isRecording) {
      stopRecording();
    }
  };

  const mediaOptions = [
    { icon: <ImageIcon color="primary" />, type: "image", label: "Image" },
    { icon: <MovieIcon color="primary" />, type: "video", label: "Video" },
    {
      icon: <AudiotrackIcon color="primary" />,
      type: "audio",
      label: "Upload Audio",
    },
    {
      icon: <DescriptionIcon color="primary" />,
      type: "document",
      label: "Document",
    },
    {
      icon: isRecording ? (
        <StopIcon color="error" />
      ) : (
        <MicIcon color="primary" />
      ),
      type: "audio-record",
      label: isRecording ? "Stop Recording" : "Record Audio",
    },
  ];

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {selectedFile && (
        <Chip
          label={`${selectedFile.name}${
            isRecording ? ` (${formatRecordingTime(recordingTime)})` : ""
          }`}
          onDelete={removeFile}
          color="primary"
          sx={{ position: "absolute", top: -40, left: 0, maxWidth: "80%" }}
          deleteIcon={<CloseIcon />}
        />
      )}

      <Paper
        elevation={0}
        sx={{
          p: 1,
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          width: "100%",
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(4px)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
          },
        }}
      >
        {/* Emoji button */}
        <Tooltip title="Add emoji" arrow>
          <IconButton
            onClick={handleOpenEmojiMenu}
            sx={{
              alignSelf: "center",
              color: emojiOpen ? "primary.main" : "text.secondary",
              transition: "all 0.2s",
              "&:hover": {
                color: "primary.main",
                transform: "scale(1.1)",
              },
            }}
            aria-describedby="emoji-popover"
          >
            <MoodIcon />
          </IconButton>
        </Tooltip>

        {/* Plus button for media menu */}
        <Tooltip title="Attach media" arrow>
          <IconButton
            onClick={handleOpenMediaMenu}
            sx={{
              alignSelf: "center",
              color: mediaOpen ? "primary.main" : "text.secondary",
              transition: "all 0.2s",
              "&:hover": {
                color: "primary.main",
                transform: "scale(1.1)",
              },
            }}
            aria-describedby="media-popover"
          >
            {isRecording ? (
              <Box sx={{ position: "relative" }}>
                <MicIcon color="error" />
                <CircularProgress
                  size={36}
                  thickness={4}
                  value={(recordingTime % 30) * (100 / 30)}
                  variant="determinate"
                  sx={{
                    position: "absolute",
                    top: -6,
                    left: -6,
                    color: "error.main",
                  }}
                />
              </Box>
            ) : (
              <AddIcon />
            )}
          </IconButton>
        </Tooltip>

        {/* Text input */}
        <TextField
          inputRef={inputRef}
          variant="outlined"
          multiline
          fullWidth
          maxRows={maxRows}
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyPress}
          sx={{
            flexGrow: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              padding: "8px 16px",
              backgroundColor: "background.paper",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.03)",
              },
              "&.Mui-focused": {
                backgroundColor: "rgba(25, 118, 210, 0.05)",
                boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "0",
              fontSize: "0.95rem",
              "&::placeholder": {
                opacity: 0.7,
              },
            },
          }}
          InputProps={{
            sx: {
              "& fieldset": {
                borderColor: "rgba(0, 0, 0, 0.12)",
                transition: "border-color 0.3s ease",
              },
              "&:hover fieldset": {
                borderColor: "rgba(0, 0, 0, 0.23)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
                borderWidth: "1px",
              },
            },
          }}
        />

        {/* Send button */}
        <Tooltip title="Send" arrow>
          <span>
            <IconButton
              color="primary"
              disabled={!text.trim() && !selectedFile}
              onClick={trySend}
              sx={{
                alignSelf: "center",
                backgroundColor:
                  text.trim() || selectedFile
                    ? "primary.main"
                    : "action.disabledBackground",
                color: "common.white",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor:
                    text.trim() || selectedFile
                      ? "primary.dark"
                      : "action.disabledBackground",
                  transform: "scale(1.05)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>

        {/* Emoji picker popover */}
        <Popover
          id="emoji-popover"
          open={emojiOpen}
          anchorEl={emojiAnchorEl}
          onClose={() => setEmojiAnchorEl(null)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          TransitionComponent={Fade}
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              border: "1px solid rgba(0,0,0,0.05)",
            },
          }}
        >
          <Box
            className="emoji-picker-container"
            sx={{ width: 350, height: 380 }}
          >
            <Picker
              data={emojiData}
              onEmojiSelect={addEmoji}
              previewPosition="none"
              skinTonePosition="none"
              theme="light"
              navPosition="bottom"
              searchPosition="sticky"
              dynamicWidth={true}
              perLine={8}
              emojiSize={24}
              searchPlaceholder="Search emojis..."
            />
          </Box>
        </Popover>

        {/* Media selection popover */}
        <Popover
          id="media-popover"
          open={mediaOpen}
          anchorEl={mediaAnchorEl}
          onClose={() => setMediaAnchorEl(null)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          TransitionComponent={Fade}
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              minWidth: 200,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.05)",
            },
          }}
        >
          <Box sx={{ p: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1,
                backgroundColor: "rgba(0, 0, 0, 0.02)",
              }}
            >
              <Box sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                Attach File
              </Box>
              <IconButton size="small" onClick={() => setMediaAnchorEl(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ my: 0 }} />
            <List dense>
              {mediaOptions.map(({ icon, type, label }) => (
                <ListItem
                  button
                  key={type}
                  onClick={() => handleMediaSelect(type)}
                  sx={{
                    borderRadius: 1,
                    py: 1.5,
                    px: 2,
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color:
                        type === "audio-record" && isRecording
                          ? "error.main"
                          : "primary.main",
                    }}
                  >
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color:
                        type === "audio-record" && isRecording
                          ? "error.main"
                          : "inherit",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Popover>
      </Paper>

      {isRecording && (
        <Box
          sx={{
            position: "absolute",
            top: -40,
            right: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "error.light",
            color: "error.contrastText",
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
          }}
        >
          <MicIcon fontSize="small" />
          <Typography variant="caption">
            {formatRecordingTime(recordingTime)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SendMessage;
